import React, { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { bff } from '@/lib/http/bff'

interface YoutubeIngestSectionProps {
  onIngested?: () => void
}

interface IngestResponse {
  accepted: boolean
  scope_key: string
  run_id: string
  _stream_url: string
}

interface StageEvent {
  stage: string
  error?: string
  [key: string]: unknown
}

interface StepState {
  stepId: string
  stepStatus: string
  error?: string
  attempts?: number | null
  updatedAt?: string | null
}

const YT_REGEX = /^https?:\/\/.*(youtube\.com|youtu\.be)\/.+/i

const TERMINAL_STAGES = new Set(['done', 'completed', 'success', 'failed', 'error'])

// --- Status color semantics (best-practice: red=fail, green=ok, amber=in-flight, neutral=pending) ---
type StatusKind = 'succeeded' | 'failed' | 'running' | 'pending' | 'unknown'

function statusKind(s: string | undefined | null): StatusKind {
  const u = String(s ?? '').toUpperCase()
  if (u === 'SUCCEEDED' || u === 'SUCCESS' || u === 'OK' || u === 'COMPLETE' || u === 'COMPLETED' || u === 'DONE') return 'succeeded'
  if (u === 'FAILED' || u === 'FAILURE' || u === 'ERROR' || u === 'CANCELLED') return 'failed'
  if (u === 'RUNNING' || u === 'IN_PROGRESS' || u === 'PROCESSING' || u === 'TRIGGERING' || u === 'DISPATCHED' || u === 'STARTED') return 'running'
  if (u === 'PENDING' || u === 'QUEUED' || u === 'WAITING' || u === 'UNKNOWN' || u === '') return 'pending'
  return 'unknown'
}

const STATUS_TEXT: Record<StatusKind, string> = {
  succeeded: 'text-emerald-400',
  failed:    'text-rose-400',
  running:   'text-amber-300',
  pending:   'text-neutral-500',
  unknown:   'text-neutral-500',
}

function StatusDot({ kind }: { kind: StatusKind }) {
  if (kind === 'succeeded') return <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" aria-hidden />
  if (kind === 'failed')    return <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-400" aria-hidden />
  if (kind === 'running')   return <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" aria-hidden />
  return <span className="inline-block w-1.5 h-1.5 rounded-full bg-neutral-600" aria-hidden />
}

/**
 * YoutubeIngestSection — calls POST /bff/media-v2/youtube directly.
 * Streams progress via the media-v2 native SSE (/bff/media-v2/stream/:scope_key).
 * No legacy job system involved.
 */
export default function YoutubeIngestSection({ onIngested }: YoutubeIngestSectionProps) {
  const [url, setUrl] = useState('')
  const [lifecycleStages, setLifecycleStages] = useState<StageEvent[]>([])
  const [stepStates, setStepStates] = useState<Record<string, StepState>>({})
  const [streamDone, setStreamDone] = useState(false)
  const [streamError, setStreamError] = useState<string | null>(null)
  const esRef = useRef<EventSource | null>(null)
  const notifiedRef = useRef(false)
  const qc = useQueryClient()

  const mutation = useMutation({
    mutationFn: () =>
      bff.post<IngestResponse>('/media-v2/youtube', { url: url.trim() }),
    onSuccess: (data) => {
      setUrl('')
      setLifecycleStages([])
      setStepStates({})
      setStreamDone(false)
      setStreamError(null)
      notifiedRef.current = false
      openStream(data._stream_url)
    },
  })

  function openStream(streamUrl: string) {
    esRef.current?.close()

    // bff.post already includes /bff prefix — stream_url is a full path like /bff/media-v2/stream/:key
    const es = new EventSource(streamUrl, { withCredentials: true })
    esRef.current = es

    es.addEventListener('stage', (e: MessageEvent) => {
      try {
        const data: StageEvent = JSON.parse(e.data)
        if (String(data.stage).startsWith('step:')) {
          const stepId = String(data.stage).slice(5)
          const stepStatus = String(data.step_status ?? 'UNKNOWN')
          setStepStates((prev) => {
            const current = prev[stepId]
            const mappedError =
              typeof data.error === 'string'
                ? data.error
                : typeof data.last_error === 'string'
                  ? data.last_error
                  : undefined
            const attempts = typeof data.attempts === 'number' ? data.attempts : null
            const updatedAt = typeof data.updated_at === 'string' ? data.updated_at : null
            if (
              current &&
              current.stepStatus === stepStatus &&
              current.error === mappedError &&
              current.attempts === attempts &&
              current.updatedAt === updatedAt
            ) {
              return prev
            }
            return {
              ...prev,
              [stepId]: {
                stepId,
                stepStatus,
                error: mappedError,
                attempts,
                updatedAt,
              },
            }
          })
        } else {
          setLifecycleStages((prev) => {
            const last = prev[prev.length - 1]
            if (last?.stage === data.stage && last?.error === data.error) {
              return prev
            }
            return [...prev, data]
          })
        }
        if (TERMINAL_STAGES.has(data.stage?.toLowerCase())) {
          finish(data.stage?.toLowerCase() !== 'failed' && data.stage?.toLowerCase() !== 'error')
        }
      } catch {}
    })

    es.addEventListener('done', () => finish(true))
    es.addEventListener('snapshot', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data)
        if (data.status) {
          setLifecycleStages([{ stage: data.status, ...data }])
          finish(data.status !== 'failed')
        }
      } catch {}
    })

    es.onerror = () => {
      setStreamError('Stream disconnected')
      es.close()
    }
  }

  function finish(success: boolean) {
    esRef.current?.close()
    setStreamDone(true)
    if (success && !notifiedRef.current) {
      notifiedRef.current = true
      queueMicrotask(() => {
        qc.invalidateQueries({ queryKey: ['audioLibrary-v2'] })
        onIngested?.()
      })
    }
  }

  // Cleanup SSE on unmount
  useEffect(() => () => { esRef.current?.close() }, [])

  const isValid = YT_REGEX.test(url.trim())

  const handleSubmit = () => mutation.mutate()

  const lastStage = lifecycleStages[lifecycleStages.length - 1]
  const isFailed = lastStage?.stage?.toLowerCase() === 'failed' || lastStage?.stage?.toLowerCase() === 'error'
  const visibleSteps = Object.values(stepStates)

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && isValid && !mutation.isPending && handleSubmit()}
          placeholder="https://www.youtube.com/watch?v=..."
          className="flex-1 px-3 py-2 rounded bg-neutral-900 border border-neutral-700/60 text-sm text-neutral-100 placeholder-neutral-600 outline-none focus:border-purple-500/60 transition-colors"
          aria-label="YouTube URL"
        />
        <button
          onClick={handleSubmit}
          disabled={!isValid || mutation.isPending}
          className={`
            px-4 py-2 rounded text-sm font-medium transition-colors shrink-0
            ${isValid && !mutation.isPending
              ? 'bg-red-600 hover:bg-red-500 text-white'
              : 'bg-neutral-700 text-neutral-500 cursor-not-allowed'
            }
          `}
        >
          {mutation.isPending ? 'Sending…' : 'Ingest'}
        </button>
      </div>

      {mutation.isError && (
        <p className="text-xs text-rose-400">
          {(mutation.error as Error)?.message ?? 'Request failed'}
        </p>
      )}

      {mutation.isSuccess && (
        <div className="pac-glass rounded p-3 space-y-2">
          <div className="text-xs text-neutral-500 font-mono truncate">
            {mutation.data.scope_key}
          </div>

          {/* Stage log */}
          <div className="space-y-0.5 max-h-40 overflow-y-auto">
            {lifecycleStages.map((s, i) => {
              const k = statusKind(s.stage)
              return (
                <div key={`lifecycle-${i}`} className={`text-xs flex items-center gap-1.5 ${STATUS_TEXT[k]}`}>
                  <StatusDot kind={k} />
                  <span className="capitalize">{s.stage?.replace(/_/g, ' ')}</span>
                  {s.error && <span className="text-rose-400 truncate">— {s.error}</span>}
                </div>
              )
            })}
            {visibleSteps.map((step) => {
              const k = statusKind(step.stepStatus)
              return (
                <div key={step.stepId} className={`text-xs flex items-center gap-1.5 ${STATUS_TEXT[k]}`}>
                  <StatusDot kind={k} />
                  <span className="text-neutral-300">step {step.stepId}</span>
                  <span className="font-medium">{step.stepStatus.toLowerCase()}</span>
                  {typeof step.attempts === 'number' && step.attempts > 0 && (
                    <span className={k === 'failed' || (k === 'running' && step.attempts > 1) ? 'text-amber-400' : 'text-neutral-600'}>
                      (attempt {step.attempts}{k === 'running' && step.attempts > 1 ? ' — retrying' : ''})
                    </span>
                  )}
                  {step.error && <span className="text-rose-400 truncate" title={step.error}>— {step.error}</span>}
                  {step.updatedAt && !step.error && (
                    <span className="text-neutral-700">@ {new Date(step.updatedAt).toLocaleTimeString()}</span>
                  )}
                </div>
              )
            })}
            {!streamDone && mutation.isSuccess && (
              <div className="text-xs text-neutral-600 flex items-center gap-1.5">
                <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                </svg>
                Processing in orchestrator…
              </div>
            )}
          </div>

          {streamError && !streamDone && (
            <p className="text-xs text-amber-400">{streamError}</p>
          )}

          {streamDone && !isFailed && (
            <p className="text-xs text-emerald-400">
              ✓ Opus artifact saved — switch to Library to load it.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
